import { useEffect, useRef, useState } from 'react'

/**
 * Adds `is-in` once the element scrolls into view, so content below the fold
 * arrives rather than appearing. One observer per element, disconnected as
 * soon as it has fired — this never runs again for content already seen.
 */
export function useReveal<T extends HTMLElement = HTMLDivElement>(enabled = true) {
  const ref = useRef<T>(null)
  const [isIn, setIsIn] = useState(!enabled)

  useEffect(() => {
    if (!enabled) return
    const element = ref.current
    if (!element) return

    // Without IntersectionObserver (or with reduced motion) show it straight away.
    if (
      typeof IntersectionObserver === 'undefined' ||
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    ) {
      setIsIn(true)
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setIsIn(true)
          observer.disconnect()
        }
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 },
    )

    observer.observe(element)
    return () => observer.disconnect()
  }, [enabled])

  return { ref, isIn } as const
}
